import type { HHmmText } from '@/components/Input/InputHourMinute';
import type { CorrectionDraft } from '../-types';
function pad2(n: number): string {
  return n.toString().padStart(2, '0');
}

/** 分 → HH:MM 表記（休憩時間の入力欄で使う）。0 は空文字にせず "00:00"。 */
export function minutesToHHmm(minutes: number): HHmmText {
  const safe = Math.max(0, Math.trunc(minutes));
  return `${pad2(Math.floor(safe / 60))}:${pad2(safe % 60)}`;
}

/** HH:MM 表記 → 分。空文字は 0。 */
export function hhmmToMinutes(value: HHmmText): number {
  if (!value) return 0;
  const [h, m] = value.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}

/** DB の時刻文字列（"09:00" / null）を入力欄用の HHmmText に変換。 */
export function timeToHHmm(value: string | null): HHmmText {
  return value === null || value === '' ? '' : value as HHmmText;
}

/** 入力欄の HHmmText を DB 保存用の文字列（空は null）に変換。 */
export function hhmmToNullableTime(value: HHmmText): string | null {
  return value === '' ? null : value;
}

/** 法定所定労働時間（分）。これを超えた分を残業として算出する。 */
const STANDARD_WORK_MINUTES = 8 * 60;

/** 1 日分の労働時間集計（分）。 */
export type WorkSummary = {
  /** 総労働時間（休憩を除いた実労働） */
  totalMinutes: number;
  /** 残業時間（総労働時間が所定を超えた分） */
  overtimeMinutes: number;
  /** 深夜労働（22:00〜翌5:00 と勤務時間の重なり） */
  lateNightMinutes: number;
};

/** 2 区間 [aStart, aEnd) と [bStart, bEnd) の重なり（分）。 */
function overlapMinutes(aStart: number, aEnd: number, bStart: number, bEnd: number): number {
  return Math.max(0, Math.min(aEnd, bEnd) - Math.max(aStart, bStart));
}

/**
 * draft の出退勤・休憩から総労働 / 残業 / 深夜労働（分）を算出する。
 * 出勤または退勤が未打刻の場合は集計不可として null を返す。
 */
export function computeWorkSummary(draft: CorrectionDraft): WorkSummary | null {
  if (draft.clockIn === '' || draft.clockOut === '') return null;
  const startMinutes = hhmmToMinutes(draft.clockIn);
  let endMinutes = hhmmToMinutes(draft.clockOut);
  // 退勤が出勤以下なら日跨ぎ勤務として翌日扱いにする。
  if (endMinutes <= startMinutes) endMinutes += 24 * 60;
  const grossMinutes = endMinutes - startMinutes;
  const totalMinutes = Math.max(0, grossMinutes - draft.breakMinutes);
  const overtimeMinutes = Math.max(0, totalMinutes - STANDARD_WORK_MINUTES);

  // 深夜帯（22:00〜翌5:00 = 1320〜1740 分）を前後日含めて重ね合わせる。
  let lateNightMinutes = 0;
  for (let day = -1; day <= 1; day++) {
    lateNightMinutes += overlapMinutes(startMinutes, endMinutes, day * 24 * 60 + 22 * 60, day * 24 * 60 + 29 * 60);
  }
  return {
    totalMinutes,
    overtimeMinutes,
    lateNightMinutes
  };
}

/** 分 → 「H:MM」表記（読み取り専用の時間表示用）。 */
export function formatMinutesAsHours(minutes: number): string {
  const safe = Math.max(0, Math.trunc(minutes));
  return `${Math.floor(safe / 60)}:${pad2(safe % 60)}`;
}

/** draft が元の値から変更されているか。 */
export function isDraftEdited(original: CorrectionDraft, draft: CorrectionDraft): boolean {
  return original.clockIn !== draft.clockIn || original.clockOut !== draft.clockOut || original.breakMinutes !== draft.breakMinutes;
}